import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../layouts';
import { Images } from '../../constants';
import { Link } from 'react-router-dom';
import { Routes } from '../../routes';
import PlanSelectionModal from '../modal/PlanSelectionModal';
import { useAppSelector } from '../../stores';
import CreditsService from '../../services/api/creditsService';
import StripeService from '../../services/api/stripeService';
import PlansService from '../../services/api/plansService';
import { Spiner } from '../../components';
import { useTheme } from '../../contexts/ThemeContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useBillingRecommendation } from '../../hooks/useBillingRecommendation';

/** Corrige les valeurs legacy en base (ex. 52 crédits au lieu de 40). */
function normalizeSystemConfig(data: SystemConfig): SystemConfig {
    const fixLegacyCredits = (n: number) => (n === 52 ? 40 : n);
    return {
        ...data,
        free_credits: fixLegacyCredits(data.free_credits),
        essential_credits: fixLegacyCredits(data.essential_credits),
        topup_essential_credits: fixLegacyCredits(data.topup_essential_credits),
    };
}

interface SystemConfig {
    free_credits: number;
    essential_credits: number;
    essential_price: number;
    plus_credits_monthly: number;
    plus_price_monthly: number;
    tokens_per_credit: number;
    rollover_percentage: number;
    rollover_max_credits: number;
    topup_essential_credits: number;
    topup_essential_price: number;
    grace_period_days: number;
}

const PricingView: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const auth = useAppSelector((state) => state.auth);
    const { theme } = useTheme();
    const { getLogo } = useBranding();
    const isLightMode = theme === 'light';
    const displayLogo = getLogo(theme) || Images.logov1;
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [config, setConfig] = useState<SystemConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const { recommendation, isMicrosoftRecommended } = useBillingRecommendation();
    // États pour répliquer exactement le comportement du site web
    const [hoveredPlan, setHoveredPlan] = useState<number | null>(null);
    const [selectedPlanIndex, setSelectedPlanIndex] = useState<number | null>(2); // Plus plan highlighted by default

    // Charger la configuration système
    useEffect(() => {
        const loadSystemConfig = async () => {
            try {
                const response = await CreditsService.getSystemConfig(auth.access_token);
                if (response.success && response.data) {
                    setConfig(normalizeSystemConfig(response.data as SystemConfig));
                } else {
                    // Fallback vers valeurs par défaut si erreur
                    console.error('[PricingView] Failed to load system config, using defaults');
                    setConfig({
                        free_credits: 40,
                        essential_credits: 40,
                        essential_price: 300,
                        plus_credits_monthly: 100,
                        plus_price_monthly: 500,
                        tokens_per_credit: 5000,
                        rollover_percentage: 10,
                        rollover_max_credits: 20,
                        topup_essential_credits: 40,
                        topup_essential_price: 300,
                        grace_period_days: 7
                    });
                }
            } catch (error) {
                console.error('[PricingView] Error loading system config:', error);
                // Fallback vers valeurs par défaut
                setConfig({
                    free_credits: 40,
                    essential_credits: 40,
                    essential_price: 300,
                    plus_credits_monthly: 100,
                    plus_price_monthly: 500,
                    tokens_per_credit: 5000,
                    rollover_percentage: 10,
                    rollover_max_credits: 20,
                    topup_essential_credits: 40,
                    topup_essential_price: 300,
                    grace_period_days: 7
                });
            } finally {
                setLoading(false);
            }
        };

        if (auth.access_token) {
            loadSystemConfig();
        }
    }, [auth.access_token]);

    // Fonction helper pour remplacer les placeholders dans les traductions
    const replacePlaceholders = (text: string, values: Record<string, number | string>): string => {
        let result = text;
        Object.keys(values).forEach(key => {
            result = result.replace(new RegExp(`{{${key}}}`, 'g'), String(values[key]));
        });
        return result;
    };

    // Plans avec format EXACTEMENT identique au site web
    const plans = config ? [
        {
            id: 'FREE',
            name: t('views.pricing.plans.free.name', 'Free'),
            price: '$0',
            description: t('views.pricing.plans.free.description', 'No expiration, perfect for exploring'),
            features: [
                replacePlaceholders(t('views.pricing.plans.free.feature1'), { credits: config.free_credits }),
                t('views.pricing.plans.free.feature2', 'Email support'),
                t('views.pricing.plans.free.feature3', 'Up to 15 users')
            ],
            cta: t('views.pricing.plans.free.button'),
            highlighted: false,
            buttonAction: () => {} // FREE - pas d'action
        },
        {
            id: 'ESSENTIAL',
            name: t('views.pricing.plans.essential.name', 'Essential'),
            price: `$${config.essential_price}`,
            description: t('views.pricing.plans.essential.description', 'One-off purchase for occasional usage'),
            features: [
                replacePlaceholders(t('views.pricing.plans.essential.feature1'), { credits: config.essential_credits }),
                t('views.pricing.plans.essential.feature2', 'Top up credits at will'),
                t('views.pricing.plans.essential.feature3', 'Unlimited users'),
                t('views.pricing.plans.essential.feature4', 'Priority email support')
            ],
            cta: isMicrosoftRecommended
                ? t('views.pricing.microsoft.cta')
                : t('views.pricing.plans.essential.button'),
            highlighted: false,
            billing: 'one-time',
            buttonAction: () => {
                if (isMicrosoftRecommended && recommendation?.marketplace_url) {
                    window.open(recommendation.marketplace_url, '_blank', 'noopener,noreferrer');
                } else {
                    setSelectedPlan('ESSENTIAL');
                }
            }
        },
        {
            id: 'PLUS',
            name: t('views.pricing.plans.plus.name', 'Plus'),
            price: `$${config.plus_price_monthly}`,
            description: t('views.pricing.plans.plus.description', 'Perfect for teams driving better business performance'),
            features: [
                replacePlaceholders(t('views.pricing.plans.plus.feature1'), { credits: config.plus_credits_monthly }),
                t('views.pricing.plans.plus.feature2', 'Monthly renewal'),
                t('views.pricing.plans.plus.feature3', 'Unlimited users'),
                t('views.pricing.plans.plus.feature4', 'SSO and API integration'),
                t('views.pricing.plans.plus.feature5', 'Dedicated success manager')
            ],
            cta: isMicrosoftRecommended
                ? t('views.pricing.microsoft.cta')
                : t('views.pricing.plans.plus.button'),
            highlighted: true, // Plan mis en évidence par défaut
            billing: '/month',
            buttonAction: () => {
                if (isMicrosoftRecommended && recommendation?.marketplace_url) {
                    window.open(recommendation.marketplace_url, '_blank', 'noopener,noreferrer');
                } else {
                    setSelectedPlan('PLUS');
                }
            }
        },
        {
            id: 'ENTERPRISE',
            name: t('views.pricing.plans.enterprise.name', 'Enterprise'),
            price: t('views.pricing.units.custom', 'Custom'),
            description: t('views.pricing.plans.enterprise.description', 'For large organizations'),
            features: [
                t('views.pricing.plans.enterprise.feature1', 'Custom credits volume'),
                t('views.pricing.plans.enterprise.feature2', 'Unlimited users'),
                t('views.pricing.plans.enterprise.feature3', '24/7 phone & email support'),
                t('views.pricing.plans.enterprise.feature4', 'Enterprise analytics'),
                t('views.pricing.plans.enterprise.feature5', 'Custom integrations'),
                t('views.pricing.plans.enterprise.feature6', 'Dedicated success team'),
                t('views.pricing.plans.enterprise.feature7', 'SLA guarantees')
            ],
            cta: t('views.pricing.plans.enterprise.button'),
            highlighted: false,
            buttonAction: () => setSelectedPlan('ENTERPRISE')
        }
    ] : [];

    const handleCloseModal = () => {
        setSelectedPlan(null);
    };

    // Fonction pour gérer le paiement via PlansService (orchestration business-level)
    const handlePaymentRequest = async (plan: string, planDetails: any) => {
        try {
            console.log('[PricingView] Initiating plan activation for:', plan);
            
            // Utiliser PlansService qui gère la logique business (validations, etc.)
            const response = await PlansService.activatePlan(
                plan,
                auth.session.company_id,
                auth.access_token
            );

            if (response.success && response.data) {
                const { action, session_url } = response.data;
                
                if (action === 'redirect_checkout') {
                    // Rediriger vers Stripe Checkout
                    console.log('[PricingView] Redirecting to Stripe Checkout:', session_url);
                    window.location.href = session_url;
                } else if (action === 'manual') {
                    // Plan ENTERPRISE nécessite contact
                    console.log('[PricingView] ENTERPRISE plan requires manual contact');
                    navigate('/contact');
                } else {
                    // Autre action (ex: plan déjà activé, etc.)
                    console.log('[PricingView] Plan activation response:', response.data);
                    alert(t('views.pricing.activateSuccess'));
                }
            } else {
                console.error('[PricingView] Failed to activate plan:', response);
                const errorMessage = PlansService.formatErrorMessage(response);
                alert(t('views.pricing.activateError', { message: errorMessage }));
            }
        } catch (error) {
            console.error('[PricingView] Plan activation error:', error);
            alert(t('views.pricing.activateErrorGeneric'));
        }
    };

    if (loading) {
        return (
            <AppLayout>
                <div className="container-fluid p-4 d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                    <Spiner className="text-warning spinner-border-lg" classParent="d-flex justify-content-center align-items-center" />
                </div>
            </AppLayout>
        );
    }

    if (!config) {
        return (
            <AppLayout>
                <div className="container-fluid p-4">
                    <div className="alert alert-danger">
                        {t('views.pricing.error', 'Error loading configuration. Please try again.')}
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <div
                className={`container-fluid fixed-top mb-3 d-flex w-100 ${isLightMode ? 'bg-white border-bottom' : 'bg-black'}`}
                style={{ borderColor: isLightMode ? 'rgba(0, 0, 0, 0.08)' : undefined }}
            >
                <div className={`fs-5 mb-0 pt-2 ${isLightMode ? 'text-dark' : 'text-white'}`} style={{ height: '50px' }}>
                    <span>
                        <img className="img-fluid" src={displayLogo} alt="" style={{ maxHeight: 30, objectFit: 'contain' }} />
                    </span>
                </div>
            </div>
            <span className="mb-5 text-black">.</span>
            
            <div className="container align-items-center" style={{ marginTop: '3rem', marginBottom: '2rem' }}>
                <div className="mb-3">
                    <Link
                        to={Routes.DASHBOARDS}
                        className={`fw-bold fs-4 text-decoration-none text-uppercase ${isLightMode ? 'text-dark' : 'text-light'}`}
                    >
                        <div className="d-flex">
                            <span><i className="fa-solid fa-arrow-left"></i></span>
                            <p className="ms-1">{t('navigation.backToDashboard')}</p>
                        </div>
                    </Link>
                </div>

                {/* Banner recommandation Microsoft pour entreprises SSO - Theme-Aware Design */}
                {isMicrosoftRecommended && (
                    <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4 rounded-3" 
                             style={{ 
                                 backgroundColor: isLightMode ? 'rgba(0, 120, 212, 0.09)' : 'rgba(0, 120, 212, 0.16)',
                                 border: isLightMode ? '1px solid rgba(0, 120, 212, 0.24)' : '1px solid rgba(96, 165, 250, 0.35)',
                                 borderLeft: '4px solid #0078D4'
                             }}>
                            {/* Left: Icon + Message */}
                            <div className="d-flex align-items-center gap-3 flex-grow-1">
                                {/* Microsoft Icon */}
                                <div className="flex-shrink-0">
                                    <svg width="40" height="40" viewBox="0 0 24 24">
                                        <path fill="#F35325" d="M11.4 2v9.4H2V2h9.4z"/>
                                        <path fill="#81BC06" d="M22 2v9.4h-9.4V2H22z"/>
                                        <path fill="#05A6F0" d="M11.4 22v-9.4H2V22h9.4z"/>
                                        <path fill="#FFBA08" d="M22 22v-9.4h-9.4V22H22z"/>
                                    </svg>
                                </div>
                                
                                {/* Content */}
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        {/* <span className="badge" style={{ 
                                            backgroundColor: '#0078D4', 
                                            color: 'white',
                                            fontSize: '0.75rem' 
                                        }}>
                                            {t('views.pricing.microsoft.recommended', 'Recommandé')}
                                        </span> */}
                                        <h6 className="mb-0 fw-bold" style={{ color: isLightMode ? '#0f172a' : '#f8fafc' }}>
                                            {t('views.pricing.microsoft.title')}
                                        </h6>
                                    </div>
                                    <p className="mb-0" style={{ 
                                        fontSize: '0.9rem',
                                        opacity: 1,
                                        color: isLightMode ? '#334155' : '#e2e8f0'
                                    }}>
                                        {t('views.pricing.microsoft.shortMessage')}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Right: CTA Button */}
                            <div className="flex-shrink-0">
                                <a
                                    href={recommendation?.marketplace_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn fw-semibold px-4 py-2 d-inline-flex align-items-center gap-2"
                                    style={{
                                        backgroundColor: '#0078D4',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#005a9e';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#0078D4';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <span>{t('views.pricing.microsoft.cta')}</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {/* Section exactement identique au site web */}
                <section className="pricing-section-website" style={{ paddingTop: '2rem', paddingBottom: '1rem' }}>
                    <div className="container-xl">
                        <div className="text-center mb-4">
                            <h2 className={`display-4 fw-semibold mb-3 fade-in-up ${isLightMode ? 'text-dark' : 'text-white'}`}>
                                {t('views.pricing.title')}
                            </h2>
                            <p className={`fs-5 fade-in-up ${isLightMode ? 'text-muted' : 'text-white-50'}`} style={{animationDelay: '0.1s'}}>
                                {t('views.pricing.subtitle')}
                            </p>
                        </div>

                        <div className="row g-4 justify-content-center">
                            {plans.map((plan, index) => {
                                const isHovered = hoveredPlan === index;
                                const isSelected = selectedPlanIndex === index || plan.highlighted;
                                const isActive = isHovered || isSelected;
                                
                                return (
                                    <div key={index} className="col-lg-3 col-md-6">
                                        <div 
                                            className={`card h-100 website-card cursor-pointer ${isLightMode ? 'website-card-light' : ''} ${
                                                isActive 
                                                    ? 'card-active' 
                                                    : 'card-inactive'
                                            }`}
                                            onMouseEnter={() => setHoveredPlan(index)}
                                            onMouseLeave={() => setHoveredPlan(null)}
                                            onClick={() => setSelectedPlanIndex(index)}
                                        >
                                            <div className="card-header-website">
                                                {plan.highlighted && (
                                                    <div className="mb-2">
                                                        <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill">
                                                            {t('views.pricing.plans.popular')}
                                                        </span>
                                                    </div>
                                                )}
                                                <h5 className={`plan-title ${isActive ? 'text-white' : 'text-white-muted'}`}>
                                                    {plan.name}
                                                </h5>
                                                <p className={`plan-description ${isActive ? 'text-white-80' : 'text-white-muted'}`}>
                                                    {plan.description}
                                                </p>
                                                <div className="mt-4 price-section">
                                                    <span className={`price-large ${isActive ? 'text-white' : 'text-white-muted'}`}>
                                                        {plan.price}
                                                    </span>
                                                    {plan.price !== t('views.pricing.units.custom', 'Custom') && (
                                                        <span className={`price-unit ${isActive ? 'text-white-70' : 'text-white-muted'}`}>
                                                            {plan.billing === 'one-time' ? ' ' + t('views.pricing.units.oneTime', 'one-time') : plan.billing || '/' + t('views.pricing.units.month', 'month')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="card-content-website flex-grow-1">
                                                <ul className="features-list-website">
                                                    {plan.features.map((feature, i) => (
                                                        <li 
                                                            key={i} 
                                                            className="feature-item"
                                                            style={{
                                                                animationDelay: isActive ? `${i * 50}ms` : '0ms',
                                                                transform: isActive ? 'translateX(4px)' : 'translateX(0)',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            <i className={`fa-solid fa-check check-icon ${
                                                                isActive ? 'check-active' : 'check-inactive'
                                                            }`}></i>
                                                            <span className={`feature-text ${
                                                                isActive ? 'text-white-90' : 'text-white-muted'
                                                            }`}>
                                                                {feature}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            
                                            <div className="card-footer-website">
                                                <button 
                                                    className={`btn w-100 website-btn ${
                                                        isActive
                                                            ? 'btn-active' 
                                                            : 'btn-inactive'
                                                    }`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        plan.buttonAction();
                                                    }}
                                                >
                                                    {plan.cta}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            </div>

            {/* Modal de sélection de plan */}
            {selectedPlan && config && (
                <PlanSelectionModal
                    id="planSelectionModal"
                    selectedPlan={selectedPlan}
                    config={config as SystemConfig}
                    onClose={handleCloseModal}
                    onPaymentRequest={handlePaymentRequest}
                />
            )}
        </AppLayout>
    );
};

export default PricingView;

